import { useEffect, useRef, useState } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Box, Typography, Button } from '@mui/material';
import { useTransition } from '../context/TransitionContext.tsx';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import CtaSection from '../components/cta-section/CtaSection.tsx';
import './homepage.scss';

gsap.registerPlugin(ScrollTrigger);

function Homepage() {
  const { navigateWithTransition } = useTransition();
  const heroRef = useRef<HTMLDivElement>(null);
  const vantaggiRef = useRef<HTMLDivElement>(null);
  const serviziTitleRef = useRef<HTMLHeadingElement>(null);
  const prodottiTitleRef = useRef<HTMLHeadingElement>(null);
  const percheTitleRef = useRef<HTMLHeadingElement>(null);
  const serviziCardsRef = useRef<HTMLDivElement>(null);
  const prodottiCardsRef = useRef<HTMLDivElement>(null);
  const [countersAnimated, setCountersAnimated] = useState(false);

  // Split Text Animation
  const animateSplitText = (element: HTMLElement) => {
    const text = element.textContent || '';
    element.innerHTML = text
      .split('')
      .map(char => char === ' ' ? ' ' : `<span class="split-char">${char}</span>`)
      .join('');

    const chars = element.querySelectorAll('.split-char');
    gsap.fromTo(chars,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      }
    );
  };

  useEffect(() => {
    if (heroRef.current) {
      gsap.fromTo(heroRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
      );
    }

    // Animate split text titles
    if (serviziTitleRef.current) {
      animateSplitText(serviziTitleRef.current);
    }
    if (prodottiTitleRef.current) {
      animateSplitText(prodottiTitleRef.current);
    }
    if (percheTitleRef.current) {
      animateSplitText(percheTitleRef.current);
    }

    // Animate service cards
    if (serviziCardsRef.current) {
      const cards = serviziCardsRef.current.querySelectorAll('.servizio-card');
      gsap.fromTo(cards,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: serviziCardsRef.current,
            start: "top 80%",
            toggleActions: "play none none none"
          }
        }
      );
    }

    // Animate product cards
    if (prodottiCardsRef.current) {
      const cards = prodottiCardsRef.current.querySelectorAll('.marchio-card');
      gsap.fromTo(cards,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: prodottiCardsRef.current,
            start: "top 80%",
            toggleActions: "play none none none"
          }
        }
      );
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !countersAnimated) {
            setCountersAnimated(true);

            // Animate 20+
            const counter1 = { value: 0 };
            gsap.to(counter1, {
              value: 15,
              duration: 2,
              ease: "power2.out",
              onUpdate: () => {
                const el = document.getElementById('counter-anni');
                if (el) el.textContent = Math.floor(counter1.value) + '+';
              }
            });

            // Animate 100%
            const counter2 = { value: 0 };
            gsap.to(counter2, {
              value: 100,
              duration: 2,
              ease: "power2.out",
              onUpdate: () => {
                const el = document.getElementById('counter-prodotti');
                if (el) el.textContent = Math.floor(counter2.value) + '%';
              }
            });

            // Animate H24 (just fade in)
            gsap.fromTo('#counter-h24',
              { opacity: 0, scale: 0.5 },
              { opacity: 1, scale: 1, duration: 1, ease: "back.out(1.7)" }
            );
          }
        });
      },
      { threshold: 0.3 }
    );

    if (vantaggiRef.current) {
      observer.observe(vantaggiRef.current);
    }

    return () => observer.disconnect();
  }, [countersAnimated]);

  return (
    <>
      <Helmet>
        <title>Fabbri Agricoltura | Prodotti per l'agricoltura</title>
        <meta name="description" content="Fabbri Agricoltura - Servizi di contoterzismo agricolo professionali. Lavorazioni terreno, semine, raccolta e trasporto prodotti agricoli." />
        <meta name="keywords" content="contoterzista, servizi agricoli, lavorazioni terreno, aratura, semina, raccolta, trebbiatura" />
      </Helmet>

      <Box className="homepage-agricola">
        {/* Hero Section */}
        <Box className="hero-agricola" ref={heroRef}>
          <video
            className="hero-video-agricola"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/video/video1.mp4" type="video/mp4" />
          </video>
          <Box className="hero-overlay-agricola"></Box>
          <Container maxWidth="lg" className="hero-content-agricola">
            <Box className="hero-box-agricola">
              <img
                src="/loghi/sito/logo.png"
                alt="Fabbri Agricoltura"
                className="hero-logo-agricola"
              />
              <Box className="hero-buttons-agricola">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigateWithTransition('/servizi')}
                  className="btn-primary-agricola"
                >
                  I Servizi
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigateWithTransition('/contatti')}
                  className="btn-secondary-agricola"
                >
                  Contatti
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Servizi Preview Section */}
        <Box className="servizi-preview-section">
          <Container maxWidth="lg">
            <Typography variant="h2" className="section-title-agricola split-text" ref={serviziTitleRef}>
              I Servizi
            </Typography>
            <Typography className="section-subtitle-agricola">
              Soluzioni complete per ogni esigenza agricola
            </Typography>

            <Box className="servizi-grid-two" ref={serviziCardsRef}>
              <Box className="servizio-card">
                <Box className="servizio-icon">
                  <StorefrontIcon sx={{ fontSize: 50 }} />
                </Box>
                <Typography variant="h6" className="servizio-title">
                  Vendita Prodotti
                </Typography>
                <Typography className="servizio-desc">
                  Fitosanitari, sementi, concimi, biostimolanti, corroboranti
                </Typography>
              </Box>

              <Box className="servizio-card">
                <Box className="servizio-icon">
                  <SupportAgentIcon sx={{ fontSize: 50 }} />
                </Box>
                <Typography variant="h6" className="servizio-title">
                  Consulenza
                </Typography>
                <Typography className="servizio-desc">
                  Consulenza sul campo
                </Typography>
              </Box>
            </Box>

            <Box className="servizi-cta">
              <Button
                variant="contained"
                size="large"
                onClick={() => navigateWithTransition('/servizi')}
                className="btn-primary-agricola"
              >
                Scopri Tutti i Servizi
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Separatore Verde */}
        <Box className="section-divider" />

        {/* Prodotti Preview Section */}
        <Box className="prodotti-preview-section">
          <Container maxWidth="lg">
            <Typography variant="h2" className="section-title-agricola split-text" ref={prodottiTitleRef}>
              I Prodotti
            </Typography>
            <Typography className="section-subtitle-agricola">
              I migliori prodotti per l'agricoltura
            </Typography>

            <Box className="marchi-grid-home" ref={prodottiCardsRef}>
              <Box className="marchio-card">
                <Box className="marchio-logo">
                  <img src="/loghi/marchi/Pioneer.webp" alt="Pioneer" className="logo-img logo-invert" />
                </Box>
              </Box>

              <Box className="marchio-card">
                <Box className="marchio-logo">
                  <img src="/loghi/marchi/corteva.webp" alt="Corteva" className="logo-img" />
                </Box>
              </Box>

              <Box className="marchio-card">
                <Box className="marchio-logo">
                  <video autoPlay loop muted playsInline className="logo-video">
                    <source src="/loghi/marchi/UPL_header-logo.mp4" type="video/mp4" />
                  </video>
                </Box>
              </Box>

              <Box className="marchio-card">
                <Box className="marchio-logo">
                  <img src="/loghi/marchi/dekalb.png" alt="DEKALB" className="logo-img" />
                </Box>
              </Box>
            </Box>

            <Box className="prodotti-cta">
              <Button
                variant="contained"
                size="large"
                onClick={() => navigateWithTransition('/prodotti')}
                className="btn-primary-agricola"
              >
                Scopri Tutti i Marchi
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Chi Siamo Preview */}
        <Box className="chi-siamo-preview" ref={vantaggiRef}>
          <Container maxWidth="lg">
            <Typography variant="h2" className="section-title-agricola split-text" ref={percheTitleRef}>
              Perche' Scegliere
            </Typography>
              <Typography variant="h2" className="section-title-agricola split-text" ref={percheTitleRef}>
              Fabbri Agricoltura
            </Typography>
            <Box className="vantaggi-list">
              <Box className="vantaggio-item">
                <Typography variant="h4" className="vantaggio-number" id="counter-anni">0+</Typography>
                <Typography className="vantaggio-text">Anni di esperienza nel settore agricolo</Typography>
              </Box>
              <Box className="vantaggio-item">
                <Typography variant="h4" className="vantaggio-number" id="counter-prodotti">0%</Typography>
                <Typography className="vantaggio-text">Prodotti selezionati</Typography>
              </Box>
              <Box className="vantaggio-item">
                <Typography variant="h4" className="vantaggio-number" id="counter-h24">H24</Typography>
                <Typography className="vantaggio-text">Massima disponibilità nei momenti cruciali della campagna agricola</Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* CTA Section */}
        <CtaSection />
      </Box>
    </>
  );
}

export default Homepage;
